import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { employeeService, Employee } from '@/services/employeeService';
import LoadingSpinner from '@/components/LoadingSpinner';
import { AuthenticatedImage } from '@/components/AuthenticatedImage';
import { getEmployeeDisplayId } from '@/utils/employeeDisplay';

const genderLabels: Record<string, string> = {
  male: 'Male',
  female: 'Female',
  nonbinary: 'Non-binary',
  prefer_not_to_say: 'Prefer not to say',
};

const statusLabels: Record<string, string> = {
  active: 'Active',
  inactive: 'Inactive',
  terminated: 'Terminated',
  on_leave: 'On leave',
};

const formatDate = (value?: string | Date) => {
  if (!value) {
    return '—';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleDateString();
};

const display = (value?: string | null) => (value && String(value).trim() ? value : '—');

const Field: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div>
    <dt className="text-sm font-medium text-gray-500">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900">{value || '—'}</dd>
  </div>
);

const Profile: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState<Employee | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadProfile = async () => {
      if (!user?.id) {
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const data = await employeeService.getEmployee(String(user.id));
        setEmployee(data);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Unable to load your profile');
      } finally {
        setLoading(false);
      }
    };
    loadProfile();
  }, [user?.id]);

  const contact = employee?.contacts?.[0] as any;
  const roles = employee?.roles?.map((role) => role.name).filter(Boolean) || [];

  return (
    <>
      <Helmet>
        <title>Profile - HRMS Enterprise</title>
      </Helmet>

      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
            <p className="text-gray-600">Your employee details are read-only. Contact an administrator to request changes.</p>
          </div>
          <button
            className="btn btn-outline btn-primary self-start"
            onClick={() => navigate('/change-password')}
          >
            Change Password
          </button>
        </div>

        {loading && <LoadingSpinner />}
        {error && (
          <div className="rounded-md bg-red-50 p-4 text-sm text-red-700">{error}</div>
        )}

        {!loading && !error && employee && (
          <>
            <div className="card">
              <div className="card-body space-y-6">
                <div className="flex items-center gap-4">
                  <AuthenticatedImage
                    src={employee.photoUrl?.startsWith('http') || employee.photoUrl?.startsWith('data:') || employee.photoUrl?.startsWith('blob:') ? employee.photoUrl : undefined}
                    apiPath={employee.photoUrl ? `/employees/${employee.employeeId || employee.id}/photo` : undefined}
                    alt={`${employee.firstName} ${employee.lastName}`}
                    className="w-16 h-16 rounded-full object-cover border border-gray-200"
                    fallback={
                      <div className="w-16 h-16 rounded-full bg-blue-500 text-white flex items-center justify-center text-xl font-semibold">
                        {employee.firstName?.charAt(0)}
                        {employee.lastName?.charAt(0)}
                      </div>
                    }
                  />
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">
                      {[employee.salutation, employee.firstName, employee.middleInitial, employee.lastName, employee.suffix]
                        .filter(Boolean)
                        .join(' ')}
                    </h2>
                    <p className="text-sm text-gray-500">
                      {statusLabels[employee.status || ''] || display(employee.status)}
                    </p>
                  </div>
                </div>

                <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Field label="Preferred name" value={display(employee.preferredName)} />
                  <Field label="DRB ID" value={getEmployeeDisplayId(employee)} />
                  <Field label="Role" value={roles.length ? roles.join(', ') : '—'} />
                  <Field label="First name" value={employee.firstName} />
                  <Field label="Middle initial" value={display(employee.middleInitial)} />
                  <Field label="Last name" value={employee.lastName} />
                  <Field label="Suffix" value={display(employee.suffix)} />
                  <Field label="Initials" value={display(employee.initials)} />
                  <Field label="Search name" value={display(employee.searchName)} />
                  <Field label="Gender" value={genderLabels[employee.gender || ''] || display(employee.gender)} />
                  <Field label="Date of birth" value={formatDate(employee.dateOfBirth)} />
                  <Field label="Hire date" value={formatDate(employee.hireDate)} />
                  <Field label="Termination date" value={formatDate(employee.terminationDate)} />
                  <Field label="Department" value={display(employee.department)} />
                  <Field label="Category" value={display(employee.category)} />
                  <Field label="Active at" value={display(employee.activeAt)} />
                  <Field label="Home site" value={display(employee.homeSite)} />
                  <Field label="Pay code" value={display(employee.payCode)} />
                  <Field label="Hours per day" value={employee.hoursPerDay != null ? String(employee.hoursPerDay) : '—'} />
                  <Field label="Hours per week" value={employee.hoursPerWeek != null ? String(employee.hoursPerWeek) : '—'} />
                  <Field label="Overtime rule" value={display(employee.overtimeRule)} />
                  <Field label="SSN last 4" value={employee.ssnLast4 ? `••••${employee.ssnLast4}` : '—'} />
                  <Field
                    label="Manager"
                    value={
                      employee.manager
                        ? `${employee.manager.firstName} ${employee.manager.lastName}`
                        : '—'
                    }
                  />
                  <Field label="Status" value={statusLabels[employee.status || ''] || display(employee.status)} />
                </dl>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact information</h3>
                <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  <Field label="Work email" value={display(contact?.emailWork || contact?.email_work)} />
                  <Field label="Personal email" value={display(contact?.emailPersonal || contact?.email_personal)} />
                  <Field label="Mobile phone" value={display(contact?.phoneMobile || contact?.phone_mobile)} />
                  <Field label="Alternate phone" value={display(contact?.phoneAlternate || contact?.phone_alternate)} />
                  <Field label="Phone notes" value={display(contact?.phoneNotes || contact?.phone_notes)} />
                  <Field label="Address line 1" value={display(contact?.addressLine1 || contact?.address_line1)} />
                  <Field label="Address line 2" value={display(contact?.addressLine2 || contact?.address_line2)} />
                  <Field
                    label="City / State / ZIP"
                    value={
                      [contact?.city, contact?.state, contact?.zipCode || contact?.zip_code]
                        .filter(Boolean)
                        .join(', ') || '—'
                    }
                  />
                  <Field label="Country" value={display(contact?.country)} />
                  <Field
                    label="Emergency contact first name"
                    value={display(contact?.emergencyContactFirstName || contact?.emergency_contact_first_name)}
                  />
                  <Field
                    label="Emergency contact last name"
                    value={display(contact?.emergencyContactLastName || contact?.emergency_contact_last_name)}
                  />
                  <Field
                    label="Emergency contact phone"
                    value={display(contact?.emergencyContactPhone || contact?.emergency_contact_phone)}
                  />
                </dl>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default Profile;
